/**
 * The type scale, expressed in TypeScript rather than in a CSS `clamp()`.
 *
 * It lives in TS, not in a stylesheet, because `clamp()` cannot be asserted on in jsdom and
 * an untestable size is a size nobody is defending. Every number below is read by
 * `typeScale.test.tsx`.
 *
 * ## Three ranges, and the distance used to be what separated them
 *
 * A phone is a screen held at 30 cm; an iPad on a music desk is one at 60–80 cm; a desktop
 * is one at 60 cm with four times the glass. That premise built this file, and it produced
 * a scale sized so a chord symbol was legible from the piano bench — the brief's "~28pt",
 * enforced by a `MIN_CHORD_SYMBOL_PX` floor no width was allowed to cross.
 *
 * **The owner overrode it on 2026-09-01, for every device.** First from his phone ("the text
 * is all so big. could we make it so much smaller? that way everything will fit comfortably
 * on screen, cos it doesnt right now"), and then, when that shipped as a phone-only change,
 * again and wider: "oh no sorry i wanted desktop and ipad smaller too." So the three regimes
 * survive and the reading distances behind them are still the reason they differ from each
 * other — the phone loses a third, the iPad and the desktop a little under a quarter, and
 * the gap between them narrows rather than closing — but the absolute sizes are no longer
 * derived from 60–80 cm. They are what the person using the app asked for, which outranks a
 * number the brief inferred from a distance. See the round-eleven note in `CLAUDE.md`.
 *
 * **`MIN_CHORD_SYMBOL_PX` is gone rather than lowered**, and that is the same argument a
 * third time. It was 28 because of the bench; the new tablet segment runs 23–28, so a floor
 * at 28 would have silently held every iPad below 1366 at the old size and quietly refused
 * the request — the round-three lesson (a value set once does not outrank the person looking
 * at the screen now) with a constant in the environment variable's place. A demoted floor is
 * still a mechanism somebody has to be told about; a deleted one is not. What replaces it is
 * a *check* rather than a clamp: `typeScale.test.tsx` owns `SMALLEST_CHORD_SYMBOL_PX` and
 * asserts no chord role at any width falls under it. A check cannot silently undo anything —
 * it can only fail loudly — which is the property the floor turned out not to have.
 */

/** The tablet segment's lower anchor. Between it and `NARROW_LAYOUT_PX` nothing sits. */
const NARROW_WIDTH = 700;

/**
 * The width at or below which this is a phone, not a tablet.
 *
 * 740 sits just under 744, the smallest logical width any iPad reports (the mini in
 * portrait — the first entry in `IPAD_LOGICAL_WIDTHS`), so no iPad can reach anything gated
 * on this. That was originally a safety argument — the tablet layout could not be disturbed
 * by a phone rule — and it is now simply a separation: both sides moved on 2026-09-01, by
 * different amounts, and this is the line between the two amounts.
 *
 * **This number is written down once, here.** It decides three things and they are all one
 * decision — the type scale below, the `hc-app--stacked` class `AppFrame` stamps (which is
 * where every phone-shaped CSS rule in the app hangs from), and `BranchExpansion.stacked`
 * — because the alternative, a `@media (max-width: 740px)` beside it, is the wide
 * breakpoint's standing bug: `WIDE_LAYOUT_PX` and `tokens.css` are two copies of 1400 that
 * have to be kept in agreement by hand. Here the disagreement would be worse than
 * cosmetic. `AppFrame` re-exports it, so the import site nobody has to move stays put.
 */
export const NARROW_LAYOUT_PX = 740;

/**
 * iPad Pro 12.9" landscape — the widest logical viewport any iPad has, and where the tablet
 * segment tops out. Nothing above this width can be an iPad.
 */
export const WIDE_WIDTH = 1366;

/**
 * A desktop browser, and the point at which the scale stops growing again.
 *
 * The owner's report in round two was that on a laptop "all the text feels very compact… I
 * want it to use the full space available", which is what a scale with an iPad-shaped
 * ceiling does on a 1728-point display: it holds iPad type in the middle of a much larger
 * sheet of glass. That request has **not** been revoked — round eleven asked for smaller
 * type everywhere, not for a flat scale — so the segment still climbs above 1366; it simply
 * climbs from a lower base to a lower ceiling. A 1728-point window still gets the largest
 * type in the app, and it is about a quarter smaller than the largest type used to be.
 *
 * The step is interpolated rather than switched at a breakpoint, for the same reason the
 * iPad step is — a window being dragged wider should not jump.
 */
export const DESKTOP_WIDTH = 1800;

/**
 * The logical widths this app is actually used at. Portrait iPads first, then landscape.
 * The type-scale test walks this list; adding a device here is how you test one.
 */
export const IPAD_LOGICAL_WIDTHS: readonly number[] = Object.freeze([
  744, // iPad mini portrait
  768, // iPad 9.7"/10.2" portrait
  810, // iPad 10.2" portrait
  820, // iPad Air portrait
  834, // iPad Pro 11" portrait
  1024, // iPad 9.7"/10.2" landscape
  1080, // iPad mini landscape
  1180, // iPad Air landscape
  1194, // iPad Pro 11" landscape
  1366, // iPad Pro 12.9" landscape
]);

export interface TypeScale {
  /** The current chord. The strongest thing on the screen, by a wide margin. */
  currentChord: number;
  /** Chord symbol on a branch card. */
  branchChord: number;
  /** Chord symbol on a compressed history node. */
  historyChord: number;
  /** Sheet and screen headings. */
  heading: number;
  /** Descriptions, explanations, anything that is a sentence. */
  body: number;
  /** Relation labels, roman numerals, chips — small caps. */
  label: number;
  /** Timestamps, hints, the quietest text in the app. */
  micro: number;
}

/**
 * The phone.
 *
 * Flat rather than interpolated: every phone in portrait is between 320 and 430 points, and
 * a range that narrow does not need a curve — it needs a size. The owner's report, from his
 * phone, was "the text is all so big. could we make it so much smaller? that way everything
 * will fit comfortably on screen, cos it doesnt right now", and what he was looking at was
 * the iPad's floor: this scale used to stop shrinking at 700 px and hand a phone the sizes
 * chosen for a tablet propped on a music desk at arm's length and a half.
 *
 * The reduction is not uniform, because the complaint is not uniform. The current chord
 * loses a third, which is where the screen was actually going; the quietest labels lose
 * about a twelfth, because 11 px is where small stops being small and starts being
 * unreadable, and a phone that fits by being illegible has not fixed anything. The
 * hierarchy survives every cut — the current chord is still over 1.5× the branch cards,
 * which is the ratio the spec's "the only large thing on the screen" is measured by.
 *
 * These sizes did not move when the tablet and desktop segments came down later the same
 * day. What that did was close most of the gap: at 741 the tablet's prose is now the *same*
 * 17/14/12/11 a phone gets, and only the three chord roles are larger. Which is the honest
 * shape of the distance argument once the sizes are this small — reading distance is a claim
 * about the thing you read from across the room, and nobody has ever read a timestamp from
 * the piano bench.
 */
const PHONE_SCALE: Readonly<TypeScale> = Object.freeze({
  currentChord: 36,
  branchChord: 22,
  historyChord: 20,
  heading: 17,
  body: 14,
  label: 12,
  micro: 11,
});

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * The scale at a given viewport width.
 *
 * A phone gets `PHONE_SCALE` outright. Above it the scale is interpolated rather than
 * stepped, so a rotating iPad does not jump mid-turn, in two segments meeting at the widest
 * iPad: tablet→iPad, then iPad→desktop. Every width an iPad can report falls in the first;
 * the last only ever runs in a browser window nothing portable can produce.
 *
 * The one discontinuity in the whole function is at `NARROW_LAYOUT_PX`, and it is the
 * point: 740→744 is a gap no device occupies, and the app already changes shape across it
 * (`hc-app--stacked`). A window dragged over that line jumps once, which is honest — it has
 * become a different kind of screen.
 *
 * A non-finite width falls through to the tablet branch, where `clamp01` turns it into the
 * lower anchor. Nonsense should degrade to the size this app spent a year at, not to the
 * one added last.
 */
export function typeScaleFor(viewportWidth: number): TypeScale {
  if (viewportWidth <= NARROW_LAYOUT_PX) return { ...PHONE_SCALE };
  const t = clamp01((viewportWidth - NARROW_WIDTH) / (WIDE_WIDTH - NARROW_WIDTH));
  const d = clamp01((viewportWidth - WIDE_WIDTH) / (DESKTOP_WIDTH - WIDE_WIDTH));
  /**
   * `narrow` at `NARROW_WIDTH`, `ipad` at 1366, `desktop` at 1800 and beyond.
   *
   * `narrow` is an anchor rather than a destination — no reachable width resolves to it,
   * because the smallest viewport that gets here is 741 — and it stays at 700 because it is
   * the curve's shape, not a size anybody sees.
   *
   * Every triple below came down about a fifth to a quarter on 2026-09-01 ("oh no sorry i
   * wanted desktop and ipad smaller too"), and the cut is deliberately steepest at the top:
   * the chord roles lose 19–25 %, headings a fifth, body an eighth to a sixth, and the two
   * smallest roles only 7–14 %. That is not a taper for its own sake. The screen was being
   * spent on the big type, so that is where the room is; and 11–12 px is the floor under the
   * quiet end regardless of which device is holding it, so there was nothing there to give.
   *
   * There is no `Math.max` on the chord roles any more — see the note at the top of the
   * file. `branchChord` runs 23–28 across the tablet segment, which is under the floor that
   * used to be here, and that is the request rather than a regression.
   */
  const step = (narrow: number, ipad: number, desktop: number): number =>
    Math.round(d > 0 ? lerp(ipad, desktop, d) : lerp(narrow, ipad, t));

  return {
    currentChord: step(42, 60, 74),
    branchChord: step(23, 28, 34),
    historyChord: step(21, 25, 30),
    heading: step(17, 20, 24),
    body: step(14, 16, 19),
    label: step(12, 13, 15),
    micro: step(11, 12, 14),
  };
}

/**
 * The scale as CSS custom properties, to be spread onto a root element's `style`.
 *
 * Inline custom properties rather than a stylesheet so the value that the test reads is
 * literally the value the browser paints.
 */
export function typeScaleVars(scale: TypeScale): Record<string, string> {
  return {
    '--hc-fs-current': `${scale.currentChord}px`,
    '--hc-fs-branch': `${scale.branchChord}px`,
    '--hc-fs-history': `${scale.historyChord}px`,
    '--hc-fs-heading': `${scale.heading}px`,
    '--hc-fs-body': `${scale.body}px`,
    '--hc-fs-label': `${scale.label}px`,
    '--hc-fs-micro': `${scale.micro}px`,
  };
}
